(() => {
  const DEBUG = false;
  const PICKER_SELECTOR = 'variant-radios, variant-selects';
  const RETRIES_MS = [0, 90, 180, 350, 650, 950];

  // 0-based index of which option changes should trigger auto-fix.
  // [0] = only when Option 1 changes (recommended for your case).
  // Example: [0, 1, 2] to run when any of 3 options changes.
  const TRIGGER_OPTION_INDEXES = [0, 1];

  const variantCache = new Map();
  let internalUpdate = false;

  const log = (...args) => {
    if (DEBUG) console.log('[variant-autoselect]', ...args);
  };

  function parseJSON(text) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  function isVariantArray(data) {
    return (
      Array.isArray(data) &&
      data.length > 0 &&
      data.every(
        (v) =>
          v &&
          typeof v === 'object' &&
          Array.isArray(v.options) &&
          Object.prototype.hasOwnProperty.call(v, 'available')
      )
    );
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');
  }

  function normalizePath(raw) {
    if (!raw) return null;
    try {
      const u = new URL(raw, window.location.origin);
      return u.pathname.replace(/\/$/, '') || null;
    } catch {
      return null;
    }
  }

  function productPathFromContext(picker, sourceEl) {
    const candidates = [
      sourceEl ? sourceEl.getAttribute('data-product-url') : '',
      picker ? picker.getAttribute('data-url') : '',
      document.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
      window.location.pathname
    ];

    for (const candidate of candidates) {
      const path = normalizePath(candidate);
      if (path && path.includes('/products/')) return path;
    }

    return normalizePath(window.location.pathname) || window.location.pathname;
  }

  function getEmbeddedVariants(picker) {
    const scopes = [picker, picker.closest('product-info'), picker.closest('section')].filter(Boolean);
    const seen = new Set();

    for (const scope of scopes) {
      const scripts = scope.querySelectorAll(
        'script[data-variant-autoselect-json], script[type="application/json"]'
      );

      for (const script of scripts) {
        if (seen.has(script)) continue;
        seen.add(script);

        const parsed = parseJSON(script.textContent);
        if (isVariantArray(parsed)) return parsed;
      }
    }

    return null;
  }

  async function getAjaxVariants(productPath) {
    if (!productPath) return null;
    if (variantCache.has(productPath)) return variantCache.get(productPath);

    const request = fetch(`${productPath}.js`, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => (isVariantArray(json?.variants) ? json.variants : null))
      .catch(() => null);

    variantCache.set(productPath, request);

    const variants = await request;
    if (!variants) variantCache.delete(productPath);

    return variants;
  }

  async function getVariants(picker, sourceEl) {
    const embedded = getEmbeddedVariants(picker);
    if (embedded) {
      log('Using embedded variants:', embedded.length);
      return embedded;
    }

    const productPath = productPathFromContext(picker, sourceEl);
    const ajax = await getAjaxVariants(productPath);

    if (ajax) {
      log('Using AJAX variants:', ajax.length, productPath);
      return ajax;
    }

    log('No variants JSON found (embedded or ajax)');
    return null;
  }

  function getGroups(picker) {
    const fieldsets = Array.from(picker.querySelectorAll('fieldset')).filter((fs) =>
      fs.querySelector('input[type="radio"], select')
    );

    if (fieldsets.length) {
      return fieldsets
        .map((fs) => {
          const radios = Array.from(fs.querySelectorAll('input[type="radio"]'));
          if (radios.length) return { kind: 'radio', root: fs, radios };

          const select = fs.querySelector('select');
          if (select) return { kind: 'select', root: fs, select };

          return null;
        })
        .filter(Boolean);
    }

    return Array.from(picker.querySelectorAll('select')).map((select) => ({
      kind: 'select',
      root: select,
      select
    }));
  }

  function getGroupValue(group) {
    if (group.kind === 'radio') {
      const checked = group.radios.find((r) => r.checked);
      return checked ? checked.value : null;
    }
    return group.select.value || null;
  }

  function getChangedIndex(target, groups) {
    const byContainer = groups.findIndex((g) => g.root.contains(target));
    if (byContainer >= 0) return byContainer;

    const posAttr = target.getAttribute('data-option-position');
    if (posAttr && !Number.isNaN(Number(posAttr))) return Number(posAttr) - 1;

    const idMatch = String(target.id || '').match(/-(\d+)-\d+$/);
    if (idMatch) return Number(idMatch[1]) - 1;

    return 0;
  }

  function radioIsDisabled(radio) {
    return radio.disabled || radio.getAttribute('aria-disabled') === 'true';
  }

  function setGroupValue(group, value) {
    if (group.kind === 'radio') {
      const target = group.radios.find((r) => String(r.value).trim() === String(value).trim());
      if (!target) return false;
      if (target.checked) return false;
      if (radioIsDisabled(target)) return false;

      const label = target.id ? group.root.querySelector(`label[for="${cssEscape(target.id)}"]`) : null;
      if (label) {
        label.click();
      } else {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    if (group.select.value === value) return false;
    group.select.value = value;
    group.select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function exactAvailable(variants, selectedValues) {
    if (selectedValues.some((v) => v == null || v === '')) return false;

    return variants.some((v) => {
      if (!v.available || !Array.isArray(v.options)) return false;
      return selectedValues.every((val, i) => v.options[i] === val);
    });
  }

  function scoreVariant(variant, selectedValues, changedIndex) {
    let score = 0;
    for (let i = 0; i < selectedValues.length; i += 1) {
      if (i === changedIndex) continue;
      if (!selectedValues[i]) continue;
      if (variant.options[i] === selectedValues[i]) score += 2;
    }
    return score;
  }

  function pickBestCandidate(variants, selectedValues, changedIndex) {
    const available = variants.filter((v) => v.available && Array.isArray(v.options));
    if (!available.length) return null;

    const changedValue = selectedValues[changedIndex];
    if (!changedValue) return null;

    // Keep changed option fixed.
    const pool = available.filter((v) => v.options[changedIndex] === changedValue);
    if (!pool.length) return null;

    pool.sort(
      (a, b) => scoreVariant(b, selectedValues, changedIndex) - scoreVariant(a, selectedValues, changedIndex)
    );

    return pool[0] || null;
  }

  function getLivePicker(sectionId, fallbackPicker) {
    if (sectionId) {
      const live = document.querySelector(
        `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
      );
      if (live) return live;
    }

    if (fallbackPicker && fallbackPicker.isConnected) return fallbackPicker;
    return document.querySelector(PICKER_SELECTOR);
  }

  async function fixSelection(picker, changedIndex, sourceEl) {
    if (!picker) return;

    const groups = getGroups(picker);
    if (groups.length < 2) return;
    if (changedIndex < 0 || changedIndex >= groups.length) return;

    const selectedValues = groups.map(getGroupValue);
    if (!selectedValues[changedIndex]) return;

    const variants = await getVariants(picker, sourceEl);
    if (!variants || !variants.length) return;

    // If current combination is available, keep it.
    if (exactAvailable(variants, selectedValues)) return;

    const candidate = pickBestCandidate(variants, selectedValues, changedIndex);
    if (!candidate) return;

    internalUpdate = true;
    try {
      let changedAny = false;

      // Update ALL other option groups (works for 3+ groups)
      candidate.options.forEach((value, index) => {
        if (index === changedIndex) return;
        if (!groups[index]) return;
        const didChange = setGroupValue(groups[index], value);
        if (didChange) changedAny = true;
      });

      log('Auto-fix run', {
        changedIndex,
        changedValue: selectedValues[changedIndex],
        candidate: candidate.options,
        changedAny
      });
    } finally {
      setTimeout(() => {
        internalUpdate = false;
      }, 0);
    }
  }

  document.addEventListener('change', (event) => {
    if (internalUpdate) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target instanceof HTMLInputElement && target.type !== 'radio') return;

    const picker = target.closest(PICKER_SELECTOR);
    if (!picker) return;

    const groups = getGroups(picker);
    if (groups.length < 2) return;

    const changedIndex = getChangedIndex(target, groups);
    if (!TRIGGER_OPTION_INDEXES.includes(changedIndex)) return;

    const sectionId = picker.getAttribute('data-section') || '';

    RETRIES_MS.forEach((delay) => {
      setTimeout(() => {
        const livePicker = getLivePicker(sectionId, picker);
        fixSelection(livePicker, changedIndex, target);
      }, delay);
    });
  });
})();

